import React from 'react';
import styled from 'styled-components';
import { Modal } from 'modal-components';
import { Button } from 'primitive-components';
import { TaskResolution, TaskStatus } from './types';
import { FetchResult } from '@apollo/client';
import { useForm } from 'react-hook-form';

const ResolutionSelect = styled.select`
  padding: 6px;
  border-radius: 4px;
  width: 100%;
  height: fit-content;
  outline: 1px solid gray;
`;
const NoteHeader = styled.div`
  font-weight: bold;
`;
const NoteTextArea = styled.textarea`
  padding: 6px;
  border-radius: 4px;
  height: 100px;
  outline: 1px solid gray;
`;
const ResolveModalLayout = styled.div`
  padding-top: 1px;
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 1fr;
`;

const AlertLayout = styled.div`
  padding: 8px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
`;

export type ManualResolution = TaskResolution | TaskStatus.PAUSED;

type ResolveModalProps = {
  onSubmit: (reason: ManualResolution, note?: string) => Promise<FetchResult<unknown>>;
  closeModal: () => void;
  resolutions: ManualResolution[];
};

const resolutionLabel: { [k in ManualResolution]: string } = {
  [TaskResolution.COMPLETE]: 'Complete',
  [TaskResolution.AUTO_FIXED]: 'Fixed automatically',
  [TaskResolution.NO_ACTION_REQUIRED]: 'No action required',
  [TaskResolution.DOCUMENT_REQUIRED]: 'Document required',
  [TaskResolution.BPA_ACTION_REQUIRED]: 'BPA action required',
  [TaskResolution.BPA_ACTION_REQUIRED_NO_WORK_COMPLETE]: 'BPA action required: No Work Complete',
  [TaskResolution.SPECIALIST_ACTION_REQUIRED]: 'Specialist action required',
  [TaskResolution.THIRD_PARTY_LOGIN_UNAVAILABLE]: '3rd Party Login Unavailable',
  [TaskResolution.INSUFFICIENT_TRANSACTION_DATA]: 'Insufficient transaction data',
  [TaskStatus.PAUSED]: 'Paused',
  [TaskResolution.DUPLICATE_WORK]: 'Duplicate Work',
  [TaskResolution.OTHER_TEAM_ACTION_REQUIRED]: 'Other team action required',
  [TaskResolution.CONNECTION_DELETED]: 'No action required, connection deleted',
  [TaskResolution.CANNOT_DELETE_CONNECTION]: 'No action required, cannot delete connection',
  [TaskResolution.DOCUMENT_REQUESTED]: 'Document requested',
};

type ResolveForm = {
  reason: ManualResolution;
  note?: string;
};

const ResolveModal = ({ onSubmit, closeModal, resolutions }: ResolveModalProps) => {
  const { register, handleSubmit, formState, setError } = useForm<ResolveForm>();

  const handleSubmitEvent = async (formData: ResolveForm) => {
    if (!formData.reason) return;
    const { data, errors } = await onSubmit(formData.reason, formData.note);
    if (data) closeModal();
    if (errors) {
      setError('reason', { message: `Failed to resolve task.` });
    }
  };

  const shouldShowResolution = (resolution: ManualResolution) =>
    resolution !== TaskResolution.BPA_ACTION_REQUIRED_NO_WORK_COMPLETE;

  return (
    <Modal>
      <Modal.Header title="Manually resolve task" onClose={closeModal} />
      <form onSubmit={handleSubmit(handleSubmitEvent)}>
        <Modal.Body>
          <ResolveModalLayout>
            <ResolutionSelect
              aria-label="resolution dropdown"
              defaultValue={resolutions[0]}
              {...register('reason', { required: 'Resolution is required' })}
            >
              {resolutions
                .filter(resolutions => shouldShowResolution(resolutions))
                .map(resolution => (
                  <option key={resolution} value={resolution}>
                    {resolutionLabel[resolution]}
                  </option>
                ))}
            </ResolutionSelect>
            <NoteHeader>Notes: (optional)</NoteHeader>
            <NoteTextArea aria-label="note text area" {...register('note')}></NoteTextArea>
            {formState.errors.reason && (
              <AlertLayout>{formState.errors.reason.message}</AlertLayout>
            )}
          </ResolveModalLayout>
        </Modal.Body>
        <Modal.Footer>
          <Button
            disabled={formState.isSubmitting}
            styleType="primary"
            onClick={handleSubmit}
            type="submit"
          >
            Submit
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default ResolveModal;
